import { useTranslation } from 'react-i18next'

import {
  MetricBarStatus,
  RewardProgramDetailsDto,
  RewardProgramDetailsTierDto,
  RewardProgramEligibilityCheckDto,
  RewardWidgetStatus,
} from '@pages/RewardsHub/RewardHubDto'

import { Color } from '@ui/components/ProgressBar'

import { NameDto } from '@model/NameDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

interface UseRewardProgramTierProgressArgs {
  rewardProgramTier?: RewardProgramDetailsTierDto[]
  progress?: RewardProgramEligibilityCheckDto
  claimableTierIndex?: number
  rewardProgram?: RewardProgramDetailsDto
}

export interface TierBarItem {
  id: string
  label: string
  barColor: Color
  barPercent: number
  valueLabel: string
  showCurrencySymbol: boolean
  trackingMetricId?: number
  currency?: NameDto<string>
}

export interface TierProgressData {
  id: string
  name: string
  currency?: NameDto<string>
  metricLabel: string
  trackingMetric: NameDto
  bars: TierBarItem[]
}

interface UseRewardProgramTierProgressResult {
  shouldRender: boolean
  tiersData: TierProgressData[]
  canExpand: boolean
  claimedTierName?: string
}

type BarStatus = 'claimed' | 'completed' | 'current' | 'locked'

const buildBarItem = (params: {
  id: string
  label: string
  currentValue: number
  thresholdValue: number
  status: BarStatus
  trackingMetricId?: number
  currency?: NameDto<string>
  t: (key: string) => string
  compactNumber: (num: number, zerosAllowed?: number, stripNumeralSuffix?: boolean) => string
  showActualProgressWhenLocked?: boolean
}): TierBarItem => {
  const {
    id,
    label,
    currentValue,
    thresholdValue,
    status,
    trackingMetricId,
    currency,
    t,
    compactNumber,
    showActualProgressWhenLocked,
  } = params

  const percent = thresholdValue ? Math.min((currentValue / thresholdValue) * 100, 100) : 0

  const fmt = (num: number) => compactNumber(num, 4)

  switch (status) {
    case MetricBarStatus.CLAIMED:
      return {
        id,
        label,
        barColor: 'grey',
        barPercent: 100,
        valueLabel: t('Claimed'),
        showCurrencySymbol: false,
        trackingMetricId,
        currency,
      }
    case MetricBarStatus.COMPLETED:
      return {
        id,
        label,
        barColor: 'red',
        barPercent: 100,
        valueLabel: `${fmt(thresholdValue)}/${fmt(thresholdValue)}`,
        showCurrencySymbol: !!currency,
        trackingMetricId,
        currency,
      }
    case MetricBarStatus.CURRENT:
      return {
        id,
        label,
        barColor: 'red',
        barPercent: percent,
        valueLabel: `${fmt(currentValue)}/${fmt(thresholdValue)}`,
        showCurrencySymbol: !!currency,
        trackingMetricId,
        currency,
      }
    case MetricBarStatus.LOCKED:
    default: {
      const lockedCurrent = showActualProgressWhenLocked
        ? Math.min(currentValue, thresholdValue)
        : 0
      return {
        id,
        label,
        barColor: 'red',
        barPercent: 0,
        valueLabel: `${fmt(lockedCurrent)}/${fmt(thresholdValue)}`,
        showCurrencySymbol: !!currency,
        trackingMetricId,
        currency,
      }
    }
  }
}

const resolveClaimedOrEndedTierId = (
  isClaimedOrEnded: boolean,
  rewardProgram?: RewardProgramDetailsDto
): string | undefined => {
  if (!isClaimedOrEnded || !rewardProgram) {
    return undefined
  }

  const spins = rewardProgram.spins ?? []
  if (spins.length > 0) {
    return String(spins[0].rewardProgramTier.id)
  }

  const tradingViewPlanTierId = rewardProgram.tradingViewPlan?.rewardProgramTier?.id
  if (tradingViewPlanTierId !== undefined && tradingViewPlanTierId !== null) {
    return String(tradingViewPlanTierId)
  }

  return undefined
}

export const useRewardProgramTierProgress = (
  props: UseRewardProgramTierProgressArgs
): UseRewardProgramTierProgressResult => {
  const { t } = useTranslation()
  const { compactNumber } = useFormatNumber()

  const isClaimed = props.progress?.rewardWidgetState.id === RewardWidgetStatus.CLAIMED
  const isEnded = props.progress?.rewardWidgetState.id === RewardWidgetStatus.ENDED
  const isClaimedOrEnded = isClaimed || isEnded
  const claimedOrEndedTierId = resolveClaimedOrEndedTierId(isClaimedOrEnded, props.rewardProgram)

  const claimedOrEndedTier = props.rewardProgram?.rewardProgramInfo.tiers.find(
    (tier) => tier.id === claimedOrEndedTierId
  )

  const claimedTierName = isClaimed ? claimedOrEndedTier?.name : undefined

  const claimedOrEndedTierIndex = props.rewardProgram?.rewardProgramInfo.tiers.findIndex(
    (tier) => tier.id === String(claimedOrEndedTierId)
  )

  const hasKnownClaimedOrEndedTier =
    claimedOrEndedTierIndex !== undefined && claimedOrEndedTierIndex >= 0
  if (isClaimed && !hasKnownClaimedOrEndedTier) {
    return {
      shouldRender: false,
      tiersData: [],
      canExpand: false,
      claimedTierName,
    }
  }

  const tierCompletionFlags = (props.rewardProgramTier ?? []).map(
    (tier) => tier.currentValue >= tier.thresholdValue
  )

  const claimableTierIndex = props.claimableTierIndex ?? -1

  const isTierCompleted = (index: number) =>
    index <= claimableTierIndex || tierCompletionFlags[index]

  const tiersData: TierProgressData[] = (props.rewardProgramTier ?? []).map((tier, index) => {
    let status: BarStatus
    const trackingMetric = tier.trackingMetric

    if (hasKnownClaimedOrEndedTier) {
      if (index === claimedOrEndedTierIndex) {
        status = MetricBarStatus.CLAIMED
      } else if (index < claimedOrEndedTierIndex) {
        status = MetricBarStatus.COMPLETED
      } else {
        status = MetricBarStatus.LOCKED
      }
    } else if (isEnded) {
      status = MetricBarStatus.LOCKED
    } else {
      const allPriorTiersCompleted = Array.from({ length: index }, (_, i) => i).every(
        isTierCompleted
      )

      if (isTierCompleted(index)) {
        status = MetricBarStatus.COMPLETED
      } else if (allPriorTiersCompleted) {
        status = MetricBarStatus.CURRENT
      } else {
        status = MetricBarStatus.LOCKED
      }
    }

    const tierBar = buildBarItem({
      id: tier.id,
      label: tier.name,
      currentValue: tier.currentValue,
      thresholdValue: tier.thresholdValue,
      status,
      trackingMetricId: trackingMetric?.id,
      currency: tier.currency,
      t,
      compactNumber,
    })

    const metricBars = (tier.metrics ?? []).map((metric, metricIndex) => {
      let metricStatus: BarStatus

      if (status === MetricBarStatus.CLAIMED) {
        metricStatus = MetricBarStatus.CLAIMED
      } else if (status === MetricBarStatus.LOCKED) {
        metricStatus = MetricBarStatus.LOCKED
      } else {
        metricStatus =
          metric.currentValue >= metric.value ? MetricBarStatus.COMPLETED : MetricBarStatus.CURRENT
      }

      return buildBarItem({
        id: `${tier.id}-metric-${metricIndex}`,
        label: metric.trackingMetric.name,
        currentValue: metric.currentValue,
        thresholdValue: metric.value,
        status: metricStatus,
        trackingMetricId: metric.trackingMetric?.id,
        currency: metric.currency,
        t,
        compactNumber,
      })
    })

    return {
      id: tier.id,
      name: tier.name,
      currency: tier.currency,
      trackingMetric: trackingMetric,
      metricLabel:
        tier.metrics.length > 0
          ? tier.metrics[0]?.rewardProgramTierLogicalOperator?.name ?? '-'
          : '-',
      bars: [tierBar, ...metricBars],
    }
  })

  const canExpand = tiersData.some((tier) => tier.bars.length > 1)

  return {
    shouldRender: true,
    tiersData,
    canExpand,
    claimedTierName,
  }
}
